import { useEffect, useRef } from 'react';
import { useGetIdentity } from 'react-admin';

export const WIDGET_SRC = 'https://api.prasolmedia.com/widget.js';
/** İşletme (BUSINESS) kanalı — web/mobil müşteri taleplerinden ayrı */
export const SCRIPT_KEY = 'hd_bbcc1a7b65f7f958b6324734';
const SCRIPT_ATTR = 'data-gk-panel-helpdesk-widget';
const USER_KEY = 'gise_panel_user';
const TOKEN_KEY = 'gise_panel_access_token';
const HIDE_LAUNCHER_STYLE_ID = 'gk-panel-hd-hide-launcher';

function ensureStub() {
  if (typeof window === 'undefined') return;
  if (window.HelpdeskWidget && typeof window.HelpdeskWidget.setUser === 'function') {
    return;
  }
  window.HelpdeskWidget = {
    q: [],
    setUser(user) {
      this.q.push(user);
    },
    clearUser() {
      this.q.push({ __clear: true });
    },
    openConversation(data) {
      this.q.push({ __open: data });
    },
  };
}

function readPanelUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function syncHelpdeskUser(user) {
  ensureStub();
  const api = window.HelpdeskWidget;
  if (!api) return;

  if (!user) {
    if (typeof api.clearUser === 'function') {
      api.clearUser();
    } else if (Array.isArray(api.q)) {
      api.q.push({ __clear: true });
    }
    return;
  }

  const fullName =
    user.fullName || [user.name, user.surname].filter(Boolean).join(' ').trim() || '';

  api.setUser({
    email: user.email || '',
    fullName,
    phone: user.mobile || user.phone || '',
    lockEmail: true,
    // Widget'tan açılan talep hesaba bağlanır → Destek Taleplerim
    authToken: localStorage.getItem(TOKEN_KEY) || null,
  });
}

/** Destek Taleplerim'den açık bir talebi widget sohbetinde açar */
export function openHelpdeskConversation(thread) {
  loadWidgetScript();
  ensureStub();
  window.HelpdeskWidget.openConversation(thread);
}

/** Yüzen widget butonu panelde gösterilmez; widget AppBar'daki "Destek" ile açılır */
function hideFloatingLauncher() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HIDE_LAUNCHER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HIDE_LAUNCHER_STYLE_ID;
  style.textContent = '#hd-widget-btn{display:none !important;}';
  document.head.appendChild(style);
}

function isWidgetReady() {
  return (
    typeof window !== 'undefined' &&
    typeof window.HelpdeskWidget?.open === 'function' &&
    !!document.getElementById('hd-widget-panel')
  );
}

export function isHelpdeskWidgetOpen() {
  const panel = typeof document !== 'undefined' && document.getElementById('hd-widget-panel');
  return !!panel && panel.classList.contains('open');
}

/** AppBar "Destek" butonu: widget yüklenmediyse hazır olana kadar bekleyip açar */
export function toggleHelpdeskWidget() {
  loadWidgetScript();
  if (isWidgetReady()) {
    if (isHelpdeskWidgetOpen()) window.HelpdeskWidget.close();
    else window.HelpdeskWidget.open();
    return;
  }
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    if (isWidgetReady()) {
      clearInterval(timer);
      window.HelpdeskWidget.open();
    } else if (attempts > 50) {
      clearInterval(timer);
    }
  }, 100);
}

function loadWidgetScript() {
  if (typeof document === 'undefined') return;
  hideFloatingLauncher();
  if (document.querySelector(`script[${SCRIPT_ATTR}]`)) return;

  ensureStub();

  const el = document.createElement('script');
  el.src = WIDGET_SRC;
  el.async = true;
  el.setAttribute('data-key', SCRIPT_KEY);
  el.setAttribute(SCRIPT_ATTR, '1');
  document.body.appendChild(el);
}

/**
 * İşletme (panel) helpdesk widget — web'e değil, sadece Gise panele gömülü.
 * Yüzen buton gizli; açma/kapama AppBar'daki "Destek" butonundan.
 */
export default function HelpdeskWidget() {
  const { identity, isLoading } = useGetIdentity();
  const prevIdRef = useRef(undefined);

  useEffect(() => {
    loadWidgetScript();
  }, []);

  useEffect(() => {
    if (isLoading) return;

    if (identity?.id) {
      const cached = readPanelUser();
      syncHelpdeskUser({
        email: cached?.email || identity.email || '',
        fullName: identity.fullName || cached?.fullName || '',
        name: cached?.name,
        surname: cached?.surname,
      });
    } else if (prevIdRef.current) {
      syncHelpdeskUser(null);
    }

    prevIdRef.current = identity?.id;
  }, [identity, isLoading]);

  return null;
}
